import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { LinearProgress, Box, Button } from '@mui/material'
import { studentApiSlice } from '../../../features/student/studentSlice'
import { H3 } from '../../../layouts/TextStyles'
import { courseUserApiSlice } from '../../../features/course/courseUserSlice'
import { userApiSlice } from '../../../features/user/userSlice'
import CustomInput from '../../../components/muiComponents/customInput'
import StudentsTable from './components/StudentsTable'
import AddStudentDialog from './components/AddStudentDialog'

const StudentListPage = () => {
    const [searchParams, setSearchParams] = useSearchParams('')
    const { formatMessage } = useIntl()
    const [usersInfo, setUsersInfo] = useState([])
    const [filter, setFilter] = useState('')
    const [page, setPage] = React.useState(
        Number(searchParams.get('page')) || 1,
    )
    const [rowsPerPage, setRowsPerPage] = React.useState(
        Number(searchParams.get('size')) || 5,
    )
    const [fetchStudents, { data: studentResponse = null, isFetching }] = studentApiSlice.useLazyFetchAllStudentsQuery();

    const [deleteUser, { isLoading: isDeleting }] =
        userApiSlice.useDeleteUserMutation()
    const [getCourseUsersInfoMutation, { isLoading: isLoadingUsersInfo }] =
        courseUserApiSlice.useGetCourseUsersInfoMutation()
    const [resetTemporaryPassword, { isLoading: isReseting }] =
        userApiSlice.useResetTemporaryPasswordMutation()
    const [sendWelcomeMessage, { isLoading: isSendingWelcomeMessage }] =
        userApiSlice.useSendWelcomeMessageMutation()
    const searchStudents = (resetPage = false) => {
        if (resetPage && page !== 1) setPage(1) // поиск баскан кезде сбрасываем page, чтобы через useEffect шакырылу ушин
        else fetchStudents({ page: page - 1, size: rowsPerPage, filter });
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteUser(id).unwrap()
            toast.success(formatMessage({ id: 'deletedSuccess' }))
            searchStudents()
        } catch (error) { }
    }

    const handleResetPassword = async (id: number) => {
        try {
            await resetTemporaryPassword(id).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            searchStudents()
        } catch (error) { }
    }

    const getCourseUsersInfo = async () => {
        try {
            const result = await getCourseUsersInfoMutation(
                studentResponse.data.map((item) => item.id),
            ).unwrap()
            setUsersInfo(result)
        } catch (error) { }
    }

    const handleSendWelcomeMessage = async (id: number) => {
        try {
            await sendWelcomeMessage(id).unwrap()
            toast.success(formatMessage({ id: 'sentSuccess' }))
            searchStudents()
        } catch (error) { }
    }

    useEffect(() => {
        if (studentResponse?.data?.length) {
            getCourseUsersInfo()
        }
    }, [studentResponse])

    useEffect(() => {
        const params = {
            page: page.toString(),
            size: rowsPerPage.toString(),
        }
        setSearchParams(params)
        searchStudents()
    }, [rowsPerPage, page])

    useEffect(() => {
        searchStudents()
    }, []);

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'students' })}</H3>
                    </Title>

                    <AddStudentDialog refetchStudents={() => searchStudents()} />
                </Header>

                <SearchBox>
                    <CustomInput
                        sx={{ width: "400px" }}
                        margin="none"
                        type="text"
                        placeholder={formatMessage({ id: 'search' })}
                        size="small"
                        onChange={(e) => setFilter(e.target.value)}
                    />

                    <Button
                        variant="contained"
                        onClick={() => searchStudents(true)}
                    >
                        {formatMessage({ id: 'search' })}
                    </Button>
                </SearchBox>

                {isFetching || isReseting || isDeleting || isSendingWelcomeMessage ? (
                    <LinearProgress />
                ) : (
                    studentResponse && (
                        <StudentsTable
                            studentsData={studentResponse.data}
                            pageNumber={studentResponse.pageNumber}
                            totalCount={studentResponse.totalCount}
                            usersInfo={usersInfo}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            isFetching={isFetching}
                            isLoadingUsersInfo={isLoadingUsersInfo}
                            setPage={setPage}
                            setRowsPerPage={setRowsPerPage}
                            onDelete={handleDelete}
                            onResetPassword={handleResetPassword}
                            onSendWelcomeMessage={handleSendWelcomeMessage}
                        />
                    )
                )}
            </Content>
        </Wrapper>
    )
}

export default StudentListPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const SearchBox = styled(Box)`
    display: flex; 
    align-items: center;
    gap: 8px;
    margin: 8px 0;
`
